import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Alert, Box, Button, Chip, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  readIntegrationLink,
  reissueConnectionToken,
  requestFullSync,
  requestIntegrationImport,
  revokeIntegrationLink,
  setIntegrationLinkEnabled,
  updateIntegrationLink,
} from '../../api/business/endpoints';
import type { IntegrationLinkIssue } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatNumber } from '../../components/format';
import { IntegrationStatusChip } from '../../components/IntegrationStatusChip';
import { PageHeader } from '../../components/PageHeader';
import { useLanguage } from '../../i18n/LanguageContext';

import { ConnectionTokenDialog } from './ConnectionTokenDialog';
import { FirstSyncPanel } from './FirstSyncPanel';
import { IntegrationConflictsTable } from './IntegrationConflictsTable';
import { IntegrationListingsTable } from './IntegrationListingsTable';
import { IntegrationLogTable } from './IntegrationLogTable';
import { IntegrationMappingsTable } from './IntegrationMappingsTable';
import { LinkSettingsDialog } from './LinkSettingsDialog';
import { ShopOrdersTable } from './ShopOrdersTable';

type DetailTab = 'mappings' | 'listings' | 'conflicts' | 'log' | 'orders';

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
      {children}
    </Typography>
  </Grid>
);

/** One linked e-shop: how it stands, what it may do, and its items, conflicts and log. */
export const IntegrationDetailPage = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const { companyId = '', linkId = '' } = useParams();
  const permissions = usePermissions();
  const canManage = permissions.can('integrations.manage', companyId);
  const canReadOrders = permissions.can('orders.read', companyId);
  const [tab, setTab] = useState<DetailTab>('mappings');
  const [editing, setEditing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [issue, setIssue] = useState<IntegrationLinkIssue | null>(null);

  const query = useQuery({
    queryKey: ['integration-link', linkId],
    queryFn: () => readIntegrationLink(companyId, linkId),
    // While the shop sends its products the owner watches the counts grow.
    refetchInterval: (current) =>
      current.state.data?.firstSyncState === 'IMPORTING' || current.state.data?.importRequestedAt
        ? 5000
        : false,
  });
  const keys = ['integration-link', 'integration-links', 'integration-log'];
  const update = useAppMutation(updateIntegrationLink, keys, () => setEditing(false));
  const reissue = useAppMutation(reissueConnectionToken, keys, setIssue);
  const enable = useAppMutation(setIntegrationLinkEnabled, keys);
  const fullSync = useAppMutation(requestFullSync, keys);
  const revoke = useAppMutation(revokeIntegrationLink, keys, () => setRevoking(false));
  const fill = useAppMutation(requestIntegrationImport, keys, () => setImporting(false));

  const link = query.data;
  const ids = { companyId, linkId };

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/integrations')} sx={{ mb: 1 }}>
        {t('integrations.title')}
      </Button>
      <ErrorNotice
        error={query.error ?? reissue.error ?? enable.error ?? fullSync.error}
        onRetry={() => void query.refetch()}
      />
      {link ? (
        <>
          <PageHeader
            title={link.name}
            subtitle={link.shopUrl ?? t('integrations.notConnected')}
            action={
              canManage && link.status !== 'REVOKED' ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button onClick={() => setEditing(true)}>{t('common.edit')}</Button>
                  <Button disabled={reissue.isPending} onClick={() => reissue.mutate(ids)}>
                    {link.connectedAt === null ? t('detail.newToken') : t('detail.repair')}
                  </Button>
                  {link.status === 'ACTIVE' && link.firstSyncState === 'DONE' ? (
                    <Button disabled={fullSync.isPending} onClick={() => fullSync.mutate(ids)}>
                      {t('detail.fullSync')}
                    </Button>
                  ) : null}
                  {link.status === 'ACTIVE' &&
                  link.firstSyncState === 'DONE' &&
                  link.importRequestedAt === null &&
                  link.directionsOffered.includes('SHOP_TO_APP') ? (
                    <Button disabled={fill.isPending} onClick={() => setImporting(true)}>
                      {t('detail.fillFromShop')}
                    </Button>
                  ) : null}
                  {link.status === 'ACTIVE' || link.status === 'DISABLED' ? (
                    <Button
                      disabled={enable.isPending}
                      onClick={() => enable.mutate({ ...ids, enabled: link.status === 'DISABLED' })}
                    >
                      {link.status === 'DISABLED' ? t('detail.resume') : t('detail.pause')}
                    </Button>
                  ) : null}
                  <Button color="error" onClick={() => setRevoking(true)}>
                    {t('detail.revoke')}
                  </Button>
                </Stack>
              ) : undefined
            }
          />
          {link.status === 'PENDING' ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              {link.connectionTokenExpiresAt && new Date(link.connectionTokenExpiresAt) > new Date()
                ? t('detail.pendingHint', { when: formatDateTime(link.connectionTokenExpiresAt, language) })
                : t('detail.tokenExpired')}
            </Alert>
          ) : null}
          {link.fullSyncRequestedAt !== null && link.status === 'ACTIVE' ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              {t('detail.fullSyncPending', { when: formatDateTime(link.fullSyncRequestedAt, language) })}
            </Alert>
          ) : null}
          {link.importRequestedAt !== null && link.firstSyncState === 'DONE' && link.status === 'ACTIVE' ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              {t('detail.importPending', { when: formatDateTime(link.importRequestedAt, language) })}
            </Alert>
          ) : null}
          <FirstSyncPanel link={link} canManage={canManage} />
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Grid container spacing={2}>
              <Fact label={t('field.status')}>
                <IntegrationStatusChip status={link.status} />
              </Fact>
              <Fact label={t('detail.versions')}>
                {link.platformVersion
                  ? t('detail.versionsValue', {
                      platform: t(`platform.${link.platform}`),
                      version: link.platformVersion,
                      connector: link.connectorVersion ?? '—',
                    })
                  : t('common.none')}
              </Fact>
              <Fact label={t('detail.connected')}>{formatDateTime(link.connectedAt, language)}</Fact>
              <Fact label={t('integrations.lastSeen')}>{formatDateTime(link.lastSeenAt, language)}</Fact>
              <Fact label={t('detail.lastDelivered')}>{formatDateTime(link.lastDeliveredAt, language)}</Fact>
              <Fact label={t('detail.lastFullSync')}>{formatDateTime(link.lastFullSyncAt, language)}</Fact>
              <Fact label={t('linkSettings.scopes')}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {link.scopes.map((scope) => (
                    <Chip key={scope} size="small" label={t(`scope.${scope}` as const)} />
                  ))}
                </Stack>
              </Fact>
              <Fact label={t('linkSettings.allowlist')}>
                {link.ipAllowlist.length === 0 ? t('detail.anyAddress') : link.ipAllowlist.join(', ')}
              </Fact>
              <Fact label={t('integrations.mapped')}>{formatNumber(link.mapped, language)}</Fact>
              <Fact label={t('integrations.unmatched')}>{formatNumber(link.unmatched, language)}</Fact>
              <Fact label={t('integrations.openConflicts')}>
                {formatNumber(link.openConflicts, language)}
              </Fact>
              <Fact label={t('integrations.pending')}>{formatNumber(link.pendingDeliveries, language)}</Fact>
              {link.capabilities.includes('orders') ? (
                <Fact label={t('detail.ordersHistory')}>
                  {link.ordersHistoryDoneAt !== null
                    ? t('detail.ordersHistoryDone', {
                        when: formatDateTime(link.ordersHistoryDoneAt, language),
                      })
                    : link.ordersHistoryRequested
                      ? t('detail.ordersHistoryAwaited')
                      : t('common.none')}
                </Fact>
              ) : null}
            </Grid>
          </Paper>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tab} onChange={(_event, value: DetailTab) => setTab(value)}>
              <Tab value="mappings" label={t('detail.tabMappings', { count: link.unmatched })} />
              <Tab value="listings" label={t('detail.tabListings')} />
              <Tab value="conflicts" label={t('detail.tabConflicts', { count: link.openConflicts })} />
              <Tab value="log" label={t('detail.tabLog')} />
              {canReadOrders ? <Tab value="orders" label={t('detail.tabOrders')} /> : null}
            </Tabs>
          </Box>
          {tab === 'mappings' ? <IntegrationMappingsTable link={link} canManage={canManage} /> : null}
          {tab === 'listings' ? <IntegrationListingsTable link={link} canManage={canManage} /> : null}
          {tab === 'conflicts' ? <IntegrationConflictsTable link={link} canManage={canManage} /> : null}
          {tab === 'log' ? <IntegrationLogTable link={link} /> : null}
          {tab === 'orders' && canReadOrders ? (
            <ShopOrdersTable companyId={companyId} linkId={link.id} />
          ) : null}
          {editing ? (
            <LinkSettingsDialog
              link={link}
              onClose={() => setEditing(false)}
              isBusy={update.isPending}
              error={update.error}
              onSubmit={(settings) => update.mutate({ ...settings, ...ids })}
            />
          ) : null}
          {revoking ? (
            <ConfirmDialog
              title={t('detail.revokeTitle', { name: link.name })}
              message={t('detail.revokeMessage')}
              confirmLabel={t('detail.revoke')}
              onConfirm={() => revoke.mutate(ids)}
              onClose={() => setRevoking(false)}
              isBusy={revoke.isPending}
              error={revoke.error}
            />
          ) : null}
          {importing ? (
            <ConfirmDialog
              title={t('detail.fillFromShopTitle', { name: link.name })}
              message={t('detail.fillFromShopMessage')}
              confirmLabel={t('detail.fillFromShop')}
              onConfirm={() => fill.mutate(ids)}
              onClose={() => setImporting(false)}
              isBusy={fill.isPending}
              error={fill.error}
            />
          ) : null}
          {issue !== null ? <ConnectionTokenDialog issue={issue} onClose={() => setIssue(null)} /> : null}
        </>
      ) : null}
    </>
  );
};
